/**
 * DeleteAccountDialog — hard-delete confirmation. Kept simple and
 * category-oblivious: the caller supplies the deletion callback.
 */
import React, { useState } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function DeleteAccountDialog({ open, onOpenChange, account, onConfirm }) {
  const [deleting, setDeleting] = useState(false);

  const confirm = async () => {
    if (!account) return;
    setDeleting(true);
    try {
      await onConfirm(account);
      toast.success('Deleted.');
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not delete.');
    } finally {
      setDeleting(false);
    }
  };

  if (!account) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="accounts-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {account.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the account from your ledger. If it has linked
            transactions or holdings, those records may need to be
            reassigned first. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirm}
            disabled={deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="accounts-delete-confirm"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
